import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useSelector } from 'react-redux';

import StartIcon from '@mui/icons-material/Start';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Divider, IconButton, Typography } from '@mui/material';

import {
	PrimaryButton,
	RED_COLOR_SX,
} from 'src/components/common/buttons/PrimaryButton';
import ContainerLoadingIndicator from 'src/components/common/loading/ContainerLoadingIndicator';
import { snackbar } from 'src/components/common/snackbar/Snackbar';
import { IAnnotationPanelProps } from 'src/features/annotation/domain/types';
import {
	ANNOTATION_LANDMARK_LOADING_KEY,
	ANNOTATION_LOADING_KEY,
} from 'src/features/annotation/store';
import { RootState } from 'src/store';

import AnnotationForm from './AnnotationForm';
import AnnotationList from './AnnotationsList';

export let hasValidationError = false;

export const AnnotationPanel: React.FC<IAnnotationPanelProps> = ({
	rightPanelOpen,
	toggleRightPanel,
	disabled,
	currentAnnotation,
	landmarks,
	savedLandmarks,
	setSavedLandmarks,
	fabricManager,
	annotationLabels,
	setLandmarks,
	onCurrentAnnotationUpdate,
}) => {
	const isAnnotationLandmarkLoading = useSelector(
		(state: RootState) =>
			state.loading.loadingStates[ANNOTATION_LANDMARK_LOADING_KEY],
	);
	const isAnnotationDetailLoading = useSelector(
		(state: RootState) =>
			state.loading.loadingStates[ANNOTATION_LOADING_KEY],
	);
	const siteDetails = useSelector(
		(state: RootState) => state.site.siteDetails,
	);

	const [hasFormChanges, setHasFormChanges] = useState(false);
	const [hasAnnotationChanges, setHasAnnotationChanges] = useState(false);
	const [requestSave, setRequestSave] = useState<{ shouldSave: boolean }>({
		shouldSave: false,
	});
	const [collectedPromises, setCollectedPromises] = useState<
		Promise<string | undefined>[]
	>([]);

	const expectedPromisesRef = useRef<number>(0);

	const isLoading = useMemo(
		() => isAnnotationDetailLoading || isAnnotationLandmarkLoading,
		[isAnnotationDetailLoading, isAnnotationLandmarkLoading],
	);
	const hasUnsavedChanges = useMemo(
		() => hasAnnotationChanges || hasFormChanges,
		[hasFormChanges, hasAnnotationChanges],
	);

	const isAdminUserViewing = siteDetails?.roles?.some((role) =>
		['super_admin', 'admin'].includes(role),
	);

	const handleSaveAll = () => {
		setRequestSave({ shouldSave: true });
	};

	const handleValidationError = (err: boolean) => {
		hasValidationError = err;
	};

	const handleCollectPromise = useCallback(
		(promise: Promise<string | undefined>) => {
			setCollectedPromises((prev) => [...prev, promise]);
		},
		[],
	);

	useEffect(() => {
		const executeSave = async () => {
			if (
				!requestSave.shouldSave ||
				collectedPromises.length !== expectedPromisesRef.current
			) {
				return;
			}

			const results = await Promise.allSettled(collectedPromises);

			if (results.length === 0) return;

			const messages: string[] = [];

			results.forEach((result) => {
				if (
					result.status === 'fulfilled' &&
					result.value !== undefined
				) {
					messages.push(result.value);
				}
			});

			const finalMessage =
				messages.length > 0 ? messages.join(' ') : 'Nothing was saved.';

			snackbar(finalMessage, 'info');

			setRequestSave({ shouldSave: false });
			setCollectedPromises([]);
			expectedPromisesRef.current = 0;
		};

		executeSave();
	}, [requestSave, collectedPromises]);

	return (
		<Box
			sx={{
				width: rightPanelOpen ? 300 : 40,
				borderRadius: 2,
				boxShadow: 1,
				backgroundColor: 'white',
				display: 'flex',
				flexDirection: 'column',
				transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
				flexShrink: 0,
				maxHeight: '100%',
			}}
		>
			{/* Header */}
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					position: 'relative',
					p: 1,
					mt: 1,
					flexShrink: 0,
				}}
			>
				<IconButton
					onClick={toggleRightPanel}
					size='small'
					sx={{
						position: rightPanelOpen ? 'absolute' : 'static',
						left: rightPanelOpen ? 4 : 'auto',
					}}
				>
					<StartIcon
						sx={{
							transform: rightPanelOpen
								? 'rotate(0deg)'
								: 'rotate(180deg)',
							transition: 'transform 0.3s',
						}}
					/>
				</IconButton>

				{rightPanelOpen && (
					<Typography sx={{ textAlign: 'center' }}>
						Annotation Panel
					</Typography>
				)}
			</Box>

			{/* Content */}
			<Box
				sx={{
					display: rightPanelOpen ? 'block' : 'none',
					position: 'relative',
					height: '92.5%',
				}}
			>
				<ContainerLoadingIndicator
					manualLoading={isLoading}
					loadingKey='ANNOTATION_LOADING_KEY'
				/>

				{rightPanelOpen && (
					<Box
						sx={{
							p: 1,
							pt: 0,
							display: 'flex',
							flexDirection: 'column',
							gap: 1,
							position: 'relative',
							overflowY: 'auto',
							maxHeight: '100%',
							scrollbarWidth: 'thin',
						}}
					>
						{hasUnsavedChanges && !disabled && (
							<Box
								sx={{
									position: 'sticky',
									top: 0,
									backgroundColor: 'rgba(255, 138, 0, 0.2)',
									color: '#E53326',
									padding: '6px 12px',
									fontSize: '13px',
									fontWeight: 500,
									borderBottom: '1px solid rgb(250, 187, 93)',
									borderRadius: '4px',
									zIndex: 10,
									backdropFilter: 'blur(4px)',
								}}
							>
								<WarningAmberIcon
									sx={{ fontSize: 18, mr: 1 }}
								/>
								<span style={{ fontSize: '12px' }}>
									Unsaved changes detected. Changes will be
									lost unless saved.
								</span>
							</Box>
						)}

						{!disabled && (
							<PrimaryButton
								onClick={handleSaveAll}
								sx={{
									minHeight: 40,
									...RED_COLOR_SX,
									position: 'sticky',
									top: hasUnsavedChanges ? 60 : 0,
									zIndex: 10,
								}}
								disabled={isLoading || !hasUnsavedChanges}
							>
								Save
							</PrimaryButton>
						)}

						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: 2,
								mt: 1,
								pt: 1,
							}}
						>
							{!isAdminUserViewing && (
								<>
									<AnnotationForm
										currentAnnotation={currentAnnotation}
										disabled={disabled}
										isLoading={isLoading}
										annotationLabels={annotationLabels}
										onChange={setHasFormChanges}
										shouldSave={requestSave}
										onSubmit={(p) => {
											expectedPromisesRef.current += 1;
											handleCollectPromise(p);
										}}
										onValidationError={
											handleValidationError
										}
										onCurrentAnnotationUpdate={
											onCurrentAnnotationUpdate
										}
									/>

									<Divider>Annotations</Divider>
								</>
							)}

							<AnnotationList
								isLoading={isLoading}
								currentAnnotation={currentAnnotation}
								annotations={landmarks}
								savedAnnotations={savedLandmarks}
								setAnnotations={setLandmarks}
								setSavedAnnotations={setSavedLandmarks}
								fabricManager={fabricManager}
								disabled={disabled}
								onChange={setHasAnnotationChanges}
								shouldSave={requestSave}
								onSubmit={(p) => {
									expectedPromisesRef.current += 1;
									handleCollectPromise(p);
								}}
							/>
						</Box>
					</Box>
				)}
			</Box>
		</Box>
	);
};
